/**
 * Database API Client
 *
 * Handles account and customer metadata retrieval from PostgreSQL database.
 * Financial data (cases, balances) continues to come from Core API (TigerBeetle ledger).
 */

import { ConnectApiError, getAccountDataFromConnect } from '@/lib/connect-api';
import { STORAGE_KEYS } from '@ophelos/shared/constants/storage-keys';
import type { CustomerPortalAccountData } from '@/types/database-api';

export class DatabaseApiError extends Error {
  constructor(message: string, public details?: unknown) {
    super(message);
    this.name = 'DatabaseApiError';
  }
}

/**
 * Fetch masked account + customer metadata (no financial data) for the portal.
 *
 * @param accountId - Account ID (e.g., 'acct_xxx')
 * @param getAccessToken - Optional function to get JWT token for authenticated requests
 * @returns Account and customer data, or null if not found
 * @throws DatabaseApiError on any non-404 failure
 */
export async function getAccountData(
  accountId: string,
  getAccessToken?: () => Promise<string | null>
): Promise<CustomerPortalAccountData | null> {
  try {
    const authStage =
      (localStorage.getItem(STORAGE_KEYS.AUTH_STAGE) as 'limited' | 'full' | null) ?? 'limited';
    const data = await getAccountDataFromConnect(accountId, authStage, getAccessToken);
    if (!data) {
      console.warn('[database-api] No account data found for:', accountId);
      return null;
    }
    return data as CustomerPortalAccountData;

  } catch (error) {
    if (error instanceof ConnectApiError && error.status === 404) {
      return null;
    }
    console.error('[database-api] Unexpected error:', error);
    throw new DatabaseApiError(
      `Failed to fetch account data: ${error instanceof Error ? error.message : 'Unknown error'}`,
      error
    );
  }
}
